import * as React from "react";
import { Check } from "lucide-react";
import { RadioGroup, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AspectRatio } from "@/services/bedrock/dtos/DesignPackage";

interface FormatAndSizeControlsProps {
  format: "single" | "carousel";
  aspectRatio: AspectRatio | "auto";
  onFormatChange(v: "single" | "carousel"): void;
  onAspectRatioChange(v: AspectRatio | "auto"): void;
  disabled?: boolean;
}

interface CardOption<T extends string> {
  value: T;
  /** Accessible name; also the visible label unless `name` splits it. */
  label: string;
  name: string;
  detail?: string;
  tooltip?: string;
  preview: React.ReactNode;
}

// Every frame is 44px tall so the shapes compare at a glance.
const FRAME_SIZE: Record<AspectRatio, string> = {
  square_1_1: "w-11 h-11",
  portrait_4_5: "w-[35px] h-11",
  portrait_9_16: "w-[25px] h-11",
};

/** A miniature post: headshot dot and two text lines inside a frame at the given ratio. */
const PostFrame: React.FC<{ ratio: AspectRatio; className?: string }> = ({ ratio, className }) => (
  <span
    aria-hidden="true"
    className={cn(
      "flex flex-col justify-end gap-[3px] rounded-[5px] border-2 border-current bg-white p-1",
      FRAME_SIZE[ratio],
      className
    )}
  >
    <span className="size-[7px] shrink-0 rounded-full bg-current opacity-60" />
    <span className="h-[3px] w-3/4 shrink-0 rounded-full bg-current opacity-40" />
    <span className="h-[3px] w-1/2 shrink-0 rounded-full bg-current opacity-40" />
  </span>
);

/** The three sizes layered, dashed: the design picks one of them. */
const AutoFrame: React.FC = () => (
  <span aria-hidden="true" className="relative flex h-11 w-11 items-center justify-center">
    {(["square_1_1", "portrait_4_5", "portrait_9_16"] as AspectRatio[]).map((ratio, i) => (
      <span
        key={ratio}
        className={cn("absolute rounded-[5px] border-2 border-dashed border-current", FRAME_SIZE[ratio])}
        style={{ opacity: 0.35 + i * 0.3 }}
      />
    ))}
  </span>
);

/** Three offset slides, front one solid. */
const CarouselFrame: React.FC = () => (
  <span aria-hidden="true" className="relative block h-11 w-[53px]">
    <PostFrame ratio="portrait_4_5" className="absolute left-[18px] top-0 opacity-30" />
    <PostFrame ratio="portrait_4_5" className="absolute left-[9px] top-0 opacity-60" />
    <PostFrame ratio="portrait_4_5" className="absolute left-0 top-0" />
  </span>
);

const FORMAT_OPTIONS: CardOption<"single" | "carousel">[] = [
  { value: "single", label: "Single image", name: "Single image", preview: <PostFrame ratio="portrait_4_5" /> },
  { value: "carousel", label: "Carousel", name: "Carousel", detail: "2 to 5 slides", preview: <CarouselFrame /> },
];

const SIZE_OPTIONS: CardOption<AspectRatio | "auto">[] = [
  {
    value: "auto",
    label: "Auto",
    name: "Auto",
    tooltip: "The design picks the best size for the look.",
    preview: <AutoFrame />,
  },
  {
    value: "square_1_1",
    label: "Square 1:1",
    name: "Square",
    detail: "1:1",
    preview: <PostFrame ratio="square_1_1" />,
  },
  {
    value: "portrait_4_5",
    label: "Portrait 4:5",
    name: "Portrait",
    detail: "4:5",
    preview: <PostFrame ratio="portrait_4_5" />,
  },
  {
    value: "portrait_9_16",
    label: "Story 9:16",
    name: "Story",
    detail: "9:16",
    preview: <PostFrame ratio="portrait_9_16" />,
  },
];

function ChoiceCards<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: CardOption<T>[];
  onChange(value: T): void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <RadioGroup
        value={value}
        onValueChange={next => onChange(next as T)}
        disabled={disabled}
        aria-label={label}
        className="flex w-fit flex-wrap gap-2"
      >
        {options.map(option => {
          const selected = option.value === value;
          const card = (
            <label
              key={option.value}
              data-selected={selected ? "true" : "false"}
              className={cn(
                "group relative flex w-[104px] flex-col items-center gap-2 rounded-xl border bg-white px-2 pb-2.5 pt-3 text-center transition-all",
                "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50",
                selected ? "border-primary bg-primary/[0.04] ring-2 ring-primary/20" : "border-slate-200 shadow-sm",
                disabled
                  ? "cursor-default opacity-50"
                  : cn("cursor-pointer", !selected && "hover:-translate-y-px hover:border-slate-300 hover:shadow")
              )}
            >
              {/* Wrapped so the visually hidden radio sits outside the flex column and adds no gap. */}
              <span className="sr-only">
                <RadioGroup.Item value={option.value} disabled={disabled} aria-label={option.label} />
              </span>
              {selected && (
                <span
                  aria-hidden="true"
                  className="absolute right-1.5 top-1.5 flex size-4 items-center justify-center rounded-full bg-primary text-white"
                >
                  <Check className="size-2.5" strokeWidth={3} />
                </span>
              )}
              <span
                className={cn(
                  "flex h-12 items-center justify-center transition-colors",
                  selected ? "text-primary" : "text-slate-400 group-hover:text-slate-500"
                )}
              >
                {option.preview}
              </span>
              <span className="flex flex-col leading-tight">
                <span className={cn("text-sm font-semibold", selected ? "text-primary" : "text-slate-700")}>
                  {option.name}
                </span>
                {option.detail && <span className="text-[11px] text-slate-400">{option.detail}</span>}
              </span>
            </label>
          );
          if (!option.tooltip) return card;
          return (
            <Tooltip key={option.value}>
              <Tooltip.Trigger render={<span className="flex" />}>{card}</Tooltip.Trigger>
              <Tooltip.Content>{option.tooltip}</Tooltip.Content>
            </Tooltip>
          );
        })}
      </RadioGroup>
    </div>
  );
}

export const FormatAndSizeControls: React.FC<FormatAndSizeControlsProps> = ({
  format,
  aspectRatio,
  onFormatChange,
  onAspectRatioChange,
  disabled,
}) => (
  <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
    <ChoiceCards label="Format" value={format} options={FORMAT_OPTIONS} onChange={onFormatChange} disabled={disabled} />
    <ChoiceCards
      label="Size"
      value={aspectRatio}
      options={SIZE_OPTIONS}
      onChange={onAspectRatioChange}
      disabled={disabled}
    />
  </div>
);
